import { Trash2 } from 'lucide-react'
import { Button } from '../../../shared/components/ui/button'
import React, { useState } from 'react'
import { Volunteer, NonHomeownerUser, VolunteerTableRow } from '../types/volunteers.type'
import { deleteUser } from '../actions/volunteers.actions'
import { useQueryClient } from '@tanstack/react-query'
import { ADMIN } from '@/shared/constants/backend-api'
import errorToast from '../../../shared/lib/toast/error.toast'
import successToast from '../../../shared/lib/toast/success.toast'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../../shared/components/ui/dialog'

// Union type for user data (VolunteerTableRow is minimal table row from table-rows API)
type UserDataItem = Volunteer | NonHomeownerUser | VolunteerTableRow

// Helper function to get user data
const getUserData = (item: UserDataItem) => {
    return 'user' in item ? item.user : item
}

type DeleteUserButtonProps = {
    user: UserDataItem
}

function DeleteUserButton({ user }: DeleteUserButtonProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const queryClient = useQueryClient()
    
    const userData = getUserData(user)
    const userId = userData.id

    const handleDelete = async () => {
        setIsLoading(true)
        try {
            const response = await deleteUser(userId)
            if (!response.success) {
                errorToast(response.message || 'Failed to delete user')
                return
            }
            successToast(`User ${userData.fullname} deleted successfully`)
            setIsOpen(false)
            // Invalidate and refetch the users list
            queryClient.invalidateQueries({ queryKey: [ADMIN.base] })
            queryClient.invalidateQueries({ queryKey: [ADMIN.tableRows] })
        } catch (error) {
            errorToast('An unexpected error occurred while deleting the user')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button 
                    variant={"link"} 
                    size={"icon"}
                    className="w-7 h-7 bg-destructive rounded-md hover:bg-destructive/90"
                >
                    <Trash2 className="w-2 text-white" />
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Are you sure?</DialogTitle>
                    <DialogDescription>
                        This action cannot be undone. This will permanently delete the user{' '}
                        <strong>{userData.fullname}</strong> and remove all associated data.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button 
                        variant="outline" 
                        onClick={() => setIsOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        disabled={isLoading}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                        {isLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default DeleteUserButton
